import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  buildSurveyDefaults,
  buildSurveySchema,
  pruneSurveyArgs,
  resolveSurveyFields,
} from '@/lib/survey-schema';
import type { Playbook } from '@/types/provisioning';

/**
 * Renders a playbook's `survey` array as a react-hook-form + zod form.
 * `password` fields → password input, parsed enum titles → select, the rest →
 * text input. Submit yields the validated, pruned args object.
 */
export function SurveyForm({
  playbook,
  submitting,
  onSubmit,
}: {
  playbook: Playbook;
  submitting: boolean;
  onSubmit: (args: Record<string, unknown>) => void;
}): JSX.Element {
  const fields = useMemo(() => resolveSurveyFields(playbook.survey), [playbook.survey]);
  const schema = useMemo(() => buildSurveySchema(fields), [fields]);
  const defaults = useMemo(() => buildSurveyDefaults(fields), [fields]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<Record<string, string>>({
    resolver: zodResolver(schema),
    defaultValues: defaults,
  });

  function submit(values: Record<string, string>): void {
    onSubmit(pruneSurveyArgs(values, fields));
  }

  return (
    <form onSubmit={(e) => void handleSubmit(submit)(e)} className="space-y-4">
      {fields.length === 0 && (
        <p className="text-sm text-muted-foreground">
          This playbook takes no inputs — run it directly.
        </p>
      )}

      {fields.map((field) => {
        const error = errors[field.name];
        const errorId = `${field.name}-error`;

        return (
          <div key={field.name} className="space-y-1.5">
            <Label htmlFor={field.name}>
              {field.title}
              {field.required && <span className="ml-1 text-destructive">*</span>}
            </Label>

            {field.kind === 'select' && field.choices ? (
              <Select
                value={watch(field.name)}
                onValueChange={(v) =>
                  setValue(field.name, v, { shouldValidate: true, shouldDirty: true })
                }
              >
                <SelectTrigger id={field.name} aria-invalid={Boolean(error)}>
                  <SelectValue placeholder="Select…" />
                </SelectTrigger>
                <SelectContent>
                  {field.choices.map((choice) => (
                    <SelectItem key={choice} value={choice}>
                      {choice}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                id={field.name}
                type={field.kind === 'password' ? 'password' : 'text'}
                autoComplete={field.kind === 'password' ? 'new-password' : 'off'}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? errorId : undefined}
                {...register(field.name)}
              />
            )}

            {error && (
              <p id={errorId} className="text-xs text-destructive">
                {error.message}
              </p>
            )}
          </div>
        );
      })}

      <Button type="submit" disabled={submitting} className="w-full">
        {submitting ? 'Starting…' : `Run ${playbook.name}`}
      </Button>
    </form>
  );
}
